/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import {
    Box, Text, Button, Link, Image
} from 'components/ui';
import CreditCard from 'components/FrictionlessCheckout/Payment/CreditCard';
import TextInput from 'components/Inputs/TextInput/TextInput';
import ErrorConstants from 'utils/ErrorConstants';
import FormValidator from 'utils/FormValidator';
import creditCardUtils from 'utils/CreditCard';
import orderUtils from 'utils/Order';
import UI from 'utils/UI';
import { SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import checkoutApi from 'services/api/checkout';
import ErrorsUtils from 'utils/Errors';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { refreshCheckout } from 'services/Checkout';

import Modal from 'components/Modal/Modal';

class ConfirmCVVModal extends BaseClass {
    state = {
        securityCode: ''
    };

    closeModal = () => {
        this.props.showConfirmCVVModal({
            isOpen: false
        });
    };

    handleUpdateSecurityCode = e => {
        this.setState({
            securityCode: FormValidator.replaceDotSeparator(e.target.value, this.refs.securityCodeInput)
        });
    };

    handleValidateError = creditCard => code => {
        if (FormValidator.isEmpty(code)) {
            if (orderUtils.isZeroCheckout()) {
                return ErrorConstants.ERROR_CODES.CREDIT_CARD_CVV_VALIDATION;
            }

            return ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE;
        } else if (code.length < creditCardUtils.getSecurityCodeLength(creditCard?.cardType)) {
            return ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE_LENGTH;
        }

        return null;
    };

    handleClick = () => {
        this.closeModal();
        UI.scrollTo({ elementId: SECTION_NAMES.PAYMENT, hasOffset: false });
        this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.PAYMENT.path}` });
    };

    getDisabledState = pendingCreditCard => {
        return this.state.securityCode.length < creditCardUtils.getSecurityCodeLength(pendingCreditCard?.cardType);
    };

    handleSubmitClick = () => {
        const requestData = FrictionlessUtils.setRequestDataForCreditCardUpdate(this.props.pendingCreditCard, this.state.securityCode);
        decorators
            .withInterstice(checkoutApi.updateCreditCardOnOrder, INTERSTICE_DELAY_MS)(requestData, 'select')
            .then(data => {
                if (data) {
                    this.props.orderMerge('order', 'bankRewardsValidPaymentsMessage', data.bankRewardsValidPaymentsMessage);
                }

                this.props.swapPaypalToCreditCard();
                refreshCheckout()().then(() => {
                    this.props.clearPendingCreditCard().then(() => {
                        this.closeModal();
                        this.props.confirmCVVModalSuccessCallback();
                    });
                });
            })
            .catch(errorData => {
                ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
            });
    };

    render() {
        const {
            isOpen,
            pendingCreditCard,
            localization: {
                title, confirmSecurityCode, securityCodeLabel, changePaymentMethod, confirmPlaceOrder
            }
        } = this.props;
        const { securityCode } = this.state;

        return (
            <Modal
                isOpen={isOpen}
                onDismiss={this.closeModal}
                isDrawer={true}
                width={0}
            >
                <Modal.Header>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    paddingX={4}
                    paddingBottom={0}
                >
                    <Text
                        is='p'
                        children={confirmSecurityCode}
                    />
                    <Box paddingTop={[3, 4]}>
                        <CreditCard creditCard={pendingCreditCard} />
                        <Box
                            marginTop={[3, 4]}
                            marginBottom={[3, 4]}
                        >
                            <TextInput
                                label={securityCodeLabel}
                                autoComplete='cc-csc'
                                autoCorrect='off'
                                name='securityCode'
                                inputMode='numeric'
                                pattern='\d*'
                                onKeyDown={!this.state.isMobile && FormValidator.inputAcceptOnlyNumbers}
                                onPaste={FormValidator.pasteAcceptOnlyNumbers}
                                onChange={this.handleUpdateSecurityCode}
                                maxLength={creditCardUtils.getSecurityCodeLength(pendingCreditCard?.cardType)}
                                value={securityCode}
                                ref='securityCodeInput'
                                data-at={Sephora.debug.dataAt('cvv_input')}
                                dataAtError={Sephora.debug.dataAt('enter_security_code_error')}
                                validateError={this.handleValidateError(pendingCreditCard)}
                                customStyle={styles}
                                contentAfter={
                                    <Image
                                        src={'/img/ufe/icons/cvvIcon.svg'}
                                        width={32}
                                        height={20}
                                        marginRight={3}
                                        css={{ alignSelf: 'center' }}
                                    />
                                }
                            />
                        </Box>
                        <Link
                            color='blue'
                            underline={false}
                            children={changePaymentMethod}
                            onClick={this.handleClick}
                            marginBottom={4}
                        />
                    </Box>
                </Modal.Body>
                <Modal.Footer
                    paddingX={2}
                    hasShadowBorderForSMUI={true}
                >
                    <Button
                        variant='special'
                        disabled={this.getDisabledState(pendingCreditCard)}
                        onClick={this.handleSubmitClick}
                        data-at={Sephora.debug.dataAt('place_order_btn')}
                        width={'100%'}
                    >
                        {confirmPlaceOrder}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

const styles = {
    root: {
        marginBottom: 0,
        maxWidth: '183px'
    }
};

export default wrapComponent(ConfirmCVVModal, 'ConfirmCVVModal', true);
